import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { update_user } from '@/api/user';

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isProfilePage?: boolean;
}

export function EditProfileDialog({ open, onOpenChange, isProfilePage }: EditProfileDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, setUser } = useAuthStore().auth;

  const formSchema = z.object({
    name: z.string().min(1, { message: t('name_required') }),
    email: z.string().optional().refine((val) => {
      if (val === undefined || val.trim() === '') {
        return true;
      }
      return z.string().email().safeParse(val).success
    }, {
      message: t('email_invalid')
    }),
  });

  type ProfileForm = z.infer<typeof formSchema>;

  const form = useForm<ProfileForm>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
    },
  });

  const onSubmit = async (values: ProfileForm) => {
    if (!user) return;

    const noNameChanges  = user.name === values.name;
    const noEmailChanges = user.email === values.email;

    if (noNameChanges && noEmailChanges) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);

    try {
      await update_user(
        user.id,
        {
          name: noNameChanges ? null : values.name,
          email: noEmailChanges ? null : values.email
        },
        isProfilePage
      );

      const updatedUser = {
        ...user,
        name: values.name,
        email: values.email || undefined,
      };
      
      setUser(updatedUser);
      
      toast({
        title: t('profile_updated_successfully'),
      });
      
      onOpenChange(false);
    } catch {
      toast({
        variant: 'destructive',
        title: t('profile_update_failed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        form.reset();
        onOpenChange(state);
      }}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{t('edit_profile')}</DialogTitle>
          <DialogDescription>
            {t('edit_profile_description')}
          </DialogDescription>
        </DialogHeader>
        <div className='py-4'>
          <Form {...form}>
            <form
              id='profile-form'
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {t('nameLabel')}
                    </FormLabel>
                    <FormControl className='col-span-4'>
                      <Input
                        placeholder={t('name_placeholder')}
                        autoComplete='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {t('emailLabel')}
                    </FormLabel>
                    <FormControl className='col-span-4'>
                      <Input
                        placeholder={t('email_placeholder')}
                        autoComplete='off'
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button type='submit' form='profile-form' disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}